import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const portfolioData = [
  { name: 'Bitcoin', symbol: 'BTC', value: 45000, percentage: 45, color: 'hsl(38, 92%, 50%)', change: 2.34 },
  { name: 'Ethereum', symbol: 'ETH', value: 25000, percentage: 25, color: 'hsl(240, 60%, 60%)', change: -1.23 },
  { name: 'Solana', symbol: 'SOL', value: 15000, percentage: 15, color: 'hsl(270, 76%, 65%)', change: 5.67 },
  { name: 'Cardano', symbol: 'ADA', value: 10000, percentage: 10, color: 'hsl(199, 89%, 48%)', change: 0.89 },
  { name: 'Others', symbol: 'MISC', value: 5000, percentage: 5, color: 'hsl(217, 33%, 50%)', change: 1.12 },
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm">{data.name}</p>
        <p className="text-base font-semibold font-mono">${data.value.toLocaleString()}</p>
        <p className="text-xs text-muted-foreground">{data.percentage}% of portfolio</p>
      </div>
    );
  }
  return null;
};

export const PortfolioWidget = ({ className, showViewAll = true }) => {
  const [showBalance, setShowBalance] = React.useState(true);
  const totalValue = portfolioData.reduce((sum, item) => sum + item.value, 0);
  const totalChange = 2.45;

  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-2 px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Portfolio</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setShowBalance(!showBalance)}
          >
            <i className={cn("fa-solid text-sm", showBalance ? "fa-eye" : "fa-eye-slash")}></i>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        {/* Total Value */}
        <div className="text-center mb-4">
          <p className="text-sm text-muted-foreground">Total Balance</p>
          <h2 className="text-2xl font-semibold font-mono mt-1">
            {showBalance ? `$${totalValue.toLocaleString()}` : '••••••'}
          </h2>
          <Badge variant={totalChange >= 0 ? 'gain' : 'loss'} className="mt-2 text-xs">
            {totalChange >= 0 && <i className="fa-solid fa-arrow-up mr-1 text-[10px]"></i>}
            {totalChange < 0 && <i className="fa-solid fa-arrow-down mr-1 text-[10px]"></i>}
            {Math.abs(totalChange)}% today
          </Badge>
        </div>

        {/* Pie Chart */}
        <div className="h-[160px] w-full mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={portfolioData}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
              >
                {portfolioData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Asset List */}
        <div className="space-y-3">
          {portfolioData.slice(0, 4).map((asset) => (
            <div key={asset.symbol} className="flex items-center gap-3">
              <div 
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: asset.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{asset.symbol}</span>
                  <span className={cn(
                    "text-xs font-mono",
                    asset.change >= 0 ? "text-success" : "text-destructive"
                  )}>
                    {asset.change >= 0 ? '+' : ''}{asset.change}%
                  </span>
                </div>
                <Progress 
                  value={asset.percentage} 
                  className="h-1.5 mt-1"
                />
              </div>
              <span className="font-mono text-sm text-muted-foreground w-14 text-right">
                {showBalance ? `$${(asset.value / 1000).toFixed(1)}k` : '•••'}
              </span>
            </div>
          ))}
        </div>

        {/* View All Button */}
        {showViewAll && (
          <Button variant="ghost" className="w-full mt-4 text-primary text-sm h-9">
            View All Assets
            <i className="fa-solid fa-arrow-right ml-2 text-xs"></i>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PortfolioWidget;
